/**
 * AI-field chart cells.
 * ─────────────────────
 * `ai` fields with outputType=chart store validated chart JSON in their column. The cell renderer
 * (_DataEntryCellDisplay / _FieldInput) emits a `<span data-ai-chart="{json}" data-chart-kind="..">`
 * placeholder; here we hydrate each placeholder into a real chart using the shared analytics
 * `renderChart` (ApexCharts), so AI charts look identical to dashboard charts.
 *
 * The stored JSON is the engine-normalised shape `{ kind, labels, series: [{ name, data: [] }] }`
 * (see AiFieldEvaluator.ValidateChart). We reshape it into the analytics `QueryResult` contract.
 */
import { renderChart, type ChartKind } from '../analytics/charts';
import type { QueryResult, QueryResultColumn } from '../analytics/types';

interface AiChartData {
    kind?: string;
    labels?: unknown[];
    series?: Array<{ name?: string; data?: unknown[] }>;
}

// AI-field chart kinds (bar/pie/gauge/line/area) → analytics ChartKind.
function toChartKind(kind: string | null | undefined): ChartKind {
    switch ((kind ?? '').toLowerCase()) {
        case 'pie': return 'pie';
        case 'line': return 'line';
        case 'area': return 'area';
        case 'gauge': return 'donut'; // closest single-value radial in the shared renderer
        case 'bar':
        default: return 'column';
    }
}

function toNumber(v: unknown): number {
    if (typeof v === 'number') return v;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function buildResult(data: AiChartData): QueryResult | null {
    const labels = Array.isArray(data.labels) ? data.labels.map((l) => String(l)) : [];
    const series = Array.isArray(data.series) ? data.series : [];
    if (labels.length === 0 || series.length === 0) return null;

    const columns: QueryResultColumn[] = [
        { alias: 'label', kind: 'dimension', dataType: 'string', label: 'Label' },
    ];
    series.forEach((s, i) => {
        columns.push({
            alias: `m${i}`,
            kind: 'measure',
            dataType: 'number',
            label: s.name ? String(s.name) : `Series ${i + 1}`,
        });
    });

    const rows = labels.map((label, rowIdx) => {
        const row: Record<string, unknown> = { label };
        series.forEach((s, i) => {
            const arr = Array.isArray(s.data) ? s.data : [];
            row[`m${i}`] = toNumber(arr[rowIdx]);
        });
        return row;
    });

    return { columns, rows, reportType: 'summary', rowCount: rows.length, truncated: false };
}

export function mountAiFieldCharts(root: ParentNode = document): void {
    root.querySelectorAll<HTMLElement>('[data-ai-chart]:not([data-ai-chart-mounted])').forEach((host) => {
        host.dataset.aiChartMounted = '1';
        const raw = host.getAttribute('data-ai-chart');
        if (!raw) return;
        let data: AiChartData;
        try {
            data = JSON.parse(raw) as AiChartData;
        } catch {
            return; // malformed — leave the empty placeholder, never throw
        }
        const result = buildResult(data);
        if (!result) return;
        const kind = toChartKind(host.getAttribute('data-chart-kind') ?? data.kind);
        try {
            renderChart(host, result, { chartKind: kind });
        } catch {
            /* charts degrade silently — an AI cell should never break a list render */
        }
    });
}
